import React, { useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Paper,
    Typography,
    useTheme,
} from '@material-ui/core'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'
import { LoriText } from '../../navigation/layouts/Lori/Icons/LoriText'

interface Props {
    open: boolean
    onClose: () => void
    onFindDuplicates: () => void
    onMatchTenantIssues: () => void
}

export const LoriActionsModal = ({
    open,
    onClose,
    onFindDuplicates,
    onMatchTenantIssues,
}: Props) => {
    const theme = useTheme()
    const [hoveredCard, setHoveredCard] = useState<
        'duplicates' | 'issues' | null
    >(null)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: { borderRadius: 16, overflow: 'hidden' },
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    gap: theme.spacing(1.5),
                    padding: `${theme.spacing(1.5)}px ${theme.spacing(2.5)}px`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    color: theme.palette.primary.contrastText,
                    height: 80,
                }}
            >
                <LoriText
                    width={60}
                    color={theme.palette.primary.contrastText}
                />
                <Typography
                    variant="body2"
                    style={{ opacity: 0.9, letterSpacing: '0.03em' }}
                >
                    What would you like to analyze?
                </Typography>
            </div>

            {/* Option cards */}
            <DialogContent
                style={{
                    padding: theme.spacing(3),
                    display: 'flex',
                    gap: theme.spacing(2),
                }}
            >
                {/* Find Duplicates */}
                <Paper
                    elevation={hoveredCard === 'duplicates' ? 6 : 1}
                    onClick={() => {
                        onClose()
                        onFindDuplicates()
                    }}
                    onMouseEnter={() => setHoveredCard('duplicates')}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                        flex: 1,
                        padding: theme.spacing(3),
                        cursor: 'pointer',
                        borderRadius: 12,
                        border: `2px solid ${
                            hoveredCard === 'duplicates'
                                ? theme.palette.primary.main
                                : theme.palette.grey[200]
                        }`,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            backgroundColor: `${theme.palette.primary.main}18`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        <FileCopyIcon
                            style={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                            }}
                        />
                    </div>
                    <Typography
                        variant="subtitle1"
                        style={{
                            fontWeight: theme.typography.fontWeightBold,
                        }}
                    >
                        Find Duplicates
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ lineHeight: 1.5 }}
                    >
                        Scan damages in the current filter for duplicates and
                        group them for review.
                    </Typography>
                </Paper>

                {/* Match Tenant Issues */}
                <Paper
                    elevation={hoveredCard === 'issues' ? 6 : 1}
                    onClick={() => {
                        onClose()
                        onMatchTenantIssues()
                    }}
                    onMouseEnter={() => setHoveredCard('issues')}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                        flex: 1,
                        padding: theme.spacing(3),
                        cursor: 'pointer',
                        borderRadius: 12,
                        border: `2px solid ${
                            hoveredCard === 'issues'
                                ? theme.palette.secondary.main
                                : theme.palette.grey[200]
                        }`,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            backgroundColor: `${theme.palette.secondary.main}18`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        <AssignmentTurnedInIcon
                            style={{
                                fontSize: 28,
                                color: theme.palette.secondary.main,
                            }}
                        />
                    </div>
                    <Typography
                        variant="subtitle1"
                        style={{
                            fontWeight: theme.typography.fontWeightBold,
                        }}
                    >
                        Match Tenant Issues
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ lineHeight: 1.5 }}
                    >
                        Match damages in the current filter to pre-existing
                        conditions reported by tenants at move-in. This may take
                        some time — you will receive an email when complete.
                    </Typography>
                </Paper>
            </DialogContent>

            <DialogActions
                style={{
                    padding: `0 ${theme.spacing(3)}px ${theme.spacing(2)}px`,
                }}
            >
                <Button onClick={onClose} color="default">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    )
}
